import { useQuery } from "@tanstack/react-query";
import {
  qoLiveLessonRecords,
  qoStudentCurriculum,
  ILiveLessonRecord,
} from "../../utils/queryOptions";
import {
  CurriculumModule,
  CurriculumModuleStatus,
} from "../curriculum/types";
import { useAuth } from "../../hooks/useAuth";
import { Link } from "react-router";
import {
  ArrowLeft,
  GithubLogo,
  BookOpen,
  Broadcast,
  CaretDown,
  CheckCircle,
  Lock,
  Clock,
  WarningCircle,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import clsx from "clsx";
import VimeoPlayer from "../video/VimeoPlayer";

function getProgramType(educationType?: string): string {
  if (!educationType) return "";
  const lower = educationType.toLowerCase();
  if (lower.includes("ds") || lower.includes("data")) return "DS";
  if (lower.includes("fs") || lower.includes("full")) return "FS";
  return "";
}

// Her modül durumunun rozet stili + öğrenciye gösterilecek ipucu
const STATUS_STYLE: Record<
  CurriculumModuleStatus,
  { pill: string; icon: JSX.Element; hint: string }
> = {
  done: {
    pill: "bg-myGreen/10 text-myGreen border-myGreen/20",
    icon: <CheckCircle size={13} weight="fill" />,
    hint: "Bu modülü gördün",
  },
  now: {
    pill: "bg-myBlue/10 text-myBlue border-myBlue/20",
    icon: <Broadcast size={13} weight="fill" />,
    hint: "Şu an bu modüldesin",
  },
  available: {
    pill: "bg-myBlue/10 text-myBlue/80 border-myBlue/20",
    icon: <Clock size={13} weight="fill" />,
    hint: "Bu modül sana açık",
  },
  behind: {
    pill: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    icon: <WarningCircle size={13} weight="fill" />,
    hint: "Bu modülü yarım bıraktın",
  },
  soon: {
    pill: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    icon: <Clock size={13} weight="fill" />,
    hint: "Bu modül yakında gelecek",
  },
  locked: {
    pill: "bg-white/5 text-white/40 border-white/10",
    icon: <Lock size={13} weight="fill" />,
    hint: "Bu modül sana daha gelmedi",
  },
  missed: {
    pill: "bg-orange-400/10 text-orange-300 border-orange-400/20",
    icon: <WarningCircle size={13} weight="fill" />,
    hint: "Bu modülü atlamış olabilirsin",
  },
};

export default function LiveLessons() {
  const { user } = useAuth();
  const programType = getProgramType(user?.education_type);

  const {
    data: records,
    isSuccess,
    isLoading,
  } = useQuery(qoLiveLessonRecords({ programType }));

  // Modül id → modül (isim + sıra + öğrenciye göre durum) eşlemesi
  const { data: curriculum } = useQuery(qoStudentCurriculum());
  const moduleMap = useMemo(() => {
    const map = new Map<number, CurriculumModule>();
    curriculum?.modules?.forEach((m) => map.set(m.id, m));
    return map;
  }, [curriculum]);

  // Derslerde geçen tüm modüller → filtre çipleri (müfredat sırasına göre)
  const filterModules = useMemo(() => {
    if (!records) return [] as CurriculumModule[];
    const ids = new Set<number>();
    records.forEach((r) =>
      (r.related_module_ids ?? []).forEach((id) => ids.add(id))
    );
    return [...ids]
      .map((id) => moduleMap.get(id))
      .filter((m): m is CurriculumModule => !!m)
      .sort((a, b) => a.order - b.order);
  }, [records, moduleMap]);

  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);

  const visibleRecords = useMemo(() => {
    if (!records) return [];
    if (selectedModuleId == null) return records;
    return records.filter((r) =>
      (r.related_module_ids ?? []).includes(selectedModuleId)
    );
  }, [records, selectedModuleId]);

  return (
    <>
      {/* Header */}
      <div className="mb-6 pl-12 lg:pl-0">
        <h1 className="text-xl sm:text-2xl font-semibold">Canlı Dersler</h1>
        <p className="text-white/40 text-sm mt-1">
          Geçmiş canlı ders kayıtları ve materyaller.
        </p>
      </div>

      <div className="box rounded-3xl overflow-hidden">
        <div className="p-4 sm:p-6">
          {/* Modüle göre filtre */}
          {isSuccess && filterModules.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              <FilterChip
                label="Tümü"
                active={selectedModuleId == null}
                onClick={() => setSelectedModuleId(null)}
              />
              {filterModules.map((m) => (
                <FilterChip
                  key={m.id}
                  label={m.name}
                  active={selectedModuleId === m.id}
                  onClick={() =>
                    setSelectedModuleId(
                      selectedModuleId === m.id ? null : m.id
                    )
                  }
                />
              ))}
            </div>
          )}

          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-2xl h-16 animate-pulse"
                />
              ))}
            </div>
          )}

          {isSuccess && records.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Broadcast size={24} className="text-white/20" />
              </div>
              <p className="text-white/30 text-sm">
                Henüz canlı ders kaydı bulunmuyor.
              </p>
            </div>
          )}

          {isSuccess && records.length > 0 && (
            <div className="space-y-2">
              {visibleRecords.map((record) => (
                <LessonCard
                  key={record.id}
                  record={record}
                  moduleMap={moduleMap}
                />
              ))}
            </div>
          )}

          <div className="text-left mt-4">
            <Link
              to="/"
              className="inline-flex text-white/50 hover:text-myGreen font-semibold items-center gap-1.5 py-2 px-3 rounded-lg hover:bg-white/5 transition-all text-sm"
            >
              <ArrowLeft size="16" weight="bold" />
              <span>Anasayfaya geri dön</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "text-xs font-medium px-3 py-1.5 rounded-full border transition-colors",
        active
          ? "bg-myBlue/15 text-myBlue border-myBlue/30"
          : "bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white/70"
      )}
    >
      {label}
    </button>
  );
}

function ModuleBadge({ module }: { module: CurriculumModule }) {
  const style = STATUS_STYLE[module.status];
  return (
    <span
      title={`${module.name} — ${style.hint}`}
      className={clsx(
        "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border",
        style.pill
      )}
    >
      {style.icon}
      <span className="font-semibold opacity-70">{module.order}.</span>
      <span className="truncate max-w-[150px]">{module.name}</span>
    </span>
  );
}

function LessonCard({
  record,
  moduleMap,
}: {
  record: ILiveLessonRecord;
  moduleMap: Map<number, CurriculumModule>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const relatedModules = (record.related_module_ids ?? [])
    .map((id) => moduleMap.get(id))
    .filter((m): m is CurriculumModule => !!m)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="rounded-xl bg-white/[0.03] hover:bg-white/[0.06] transition-all overflow-hidden">
      {/* Header row */}
      <div
        className={clsx(
          "border-l-[3px] border-l-myBlue rounded-l-xl px-3.5 py-3 flex items-center gap-3 cursor-pointer"
        )}
        onClick={() => record.vimeo_id && setIsOpen(!isOpen)}
      >
        {/* Icon */}
        <div className="shrink-0 text-myBlue">
          <Broadcast size={20} weight="duotone" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-wider text-myBlue">
            Canlı Ders
          </span>
          <p className="font-medium text-sm leading-snug truncate">
            {record.title}
          </p>
          {/* İlgili modül rozetleri */}
          {relatedModules.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {relatedModules.map((m) => (
                <ModuleBadge key={m.id} module={m} />
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 flex items-center gap-2 self-start mt-0.5">
          {record.github_url && (
            <a
              href={record.github_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-white/5 hover:bg-white/10 text-white/50 hover:text-white/70 px-2.5 py-1 rounded-lg transition-colors"
            >
              {record.github_url.includes("github.com") ? (
                <>
                  <GithubLogo size={14} weight="fill" />
                  <span>Kod</span>
                </>
              ) : (
                <>
                  <BookOpen size={14} weight="fill" />
                  <span>Rehber</span>
                </>
              )}
            </a>
          )}
          {record.vimeo_id && (
            <div
              className={clsx(
                "text-white/30 transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            >
              <CaretDown size={16} weight="bold" />
            </div>
          )}
        </div>
      </div>

      {/* Video player */}
      {isOpen && record.vimeo_id && (
        <div className="aspect-video bg-black">
          <VimeoPlayer videoId={record.vimeo_id} />
        </div>
      )}
    </div>
  );
}
